/**
 * /earthworks/ai-quotes/new — start an AI quote draft (EW-3).
 *
 * Two kinds:
 *   Photo → demolition  1–4 building photos plus the office's footprint (length ×
 *                       width). Storeys, construction type and basement are the
 *                       AI's to read, and the office may fill any of them in now.
 *   Plans → dig         one plans PDF (or one image). The AI reads footprint,
 *                       depth, over-dig and slope, and any of them can be given now.
 * A length may carry its unit ("42 ft", "12.8 m", "40'-6\""). A bare number is
 * read in the company's units. Nothing is priced here: the API answers 202 with
 * a queued run and this page opens it. At the monthly cap the API answers 402
 * and the own-key prompt is shown instead.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { AiCapPrompt } from '../../components/ai/AiCapPrompt';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { useEwCustomers, useEwMeta, useEwSites } from '../../hooks/useEarthworks';
import { aiCapReached, useAiBudget, useCreateEwAiRun } from '../../hooks/useEwAiQuotes';
import { canAdminEw, ewRefusal } from '../../lib/earthworks';
import type { AiBudget, EwAiKind } from '../../types/ewAiQuote';
import { EW_EYEBROW, EW_INPUT, EW_PRIMARY, EwEmpty, EwField, EwFormError, EwPanel } from './EwBits';

const PHOTO_TYPES = 'image/jpeg,image/png,image/webp';
const PLAN_TYPES = 'application/pdf,image/jpeg,image/png,image/webp';
const CONSTRUCTION = [
  { value: 'wood_frame', label: 'Wood frame' },
  { value: 'masonry', label: 'Masonry / brick' },
  { value: 'steel', label: 'Steel frame' },
  { value: 'concrete', label: 'Concrete' },
];

export default function EwNewAiQuoteScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUse = canAdminEw(user?.role);
  const meta = useEwMeta().data?.meta ?? null;
  const imperial = meta?.unitSystem === 'imperial';
  const lenHint = imperial ? 'Feet, or with a unit: 42 ft, 40\'-6", 12.8 m' : 'Metres, or with a unit: 12.8 m, 42 ft';
  const customers = useEwCustomers(canUse).data ?? [];
  const budget = useAiBudget(canUse).data ?? null;

  const [kind, setKind] = useState<EwAiKind>('photo_demo');
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [capBudget, setCapBudget] = useState<Partial<AiBudget> | null>(null);
  const sites = useEwSites(customerId ? { customerId } : undefined).data?.data ?? [];
  const create = useCreateEwAiRun();

  const set = (k: string, v: string) => setInputs((p) => ({ ...p, [k]: v }));
  const fileLimit = kind === 'photo_demo' ? 4 : 1;
  const missing = useMemo(() => {
    const m: string[] = [];
    if (!customerId) m.push('customer');
    if (!files.length) m.push(kind === 'photo_demo' ? 'at least one photo' : 'the plans');
    if (kind === 'photo_demo' && !inputs.length?.trim()) m.push('building length');
    if (kind === 'photo_demo' && !inputs.width?.trim()) m.push('building width');
    return m;
  }, [customerId, files, kind, inputs]);
  const atCap = !!budget?.atCap && !budget?.key?.inUse;

  const switchKind = (k: EwAiKind) => {
    setKind(k);
    setFiles([]);
    setInputs({});
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCapBudget(null);
    if (missing.length) return setError(`Add ${missing.join(', ')}.`);
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('customer_id', customerId);
    if (siteId) fd.append('site_id', siteId);
    if (title.trim()) fd.append('title', title.trim());
    const sent: Record<string, string> = {};
    for (const [k, v] of Object.entries(inputs)) if (v.trim()) sent[k] = v.trim();
    fd.append('inputs', JSON.stringify(sent));
    for (const f of files) fd.append('files', f);
    try {
      const run = await create.mutateAsync(fd);
      if (run) navigate(`/earthworks/ai-quotes/${run.id}`, { replace: true });
    } catch (err) {
      const cap = aiCapReached(err);
      if (cap) setCapBudget(cap);
      else setError(ewRefusal(err, 'The AI quote could not be started.'));
    }
  };

  const text = (k: string, label: string, hint?: string) => (
    <EwField label={label} hint={hint}>
      <input className={EW_INPUT} value={inputs[k] ?? ''} onChange={(e) => set(k, e.target.value)} aria-label={label} />
    </EwField>
  );

  return (
    <div data-testid="ew-ai-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={EW_EYEBROW} title="New AI Quote" onBack={() => navigate(-1)} backLabel="Back"
        description="The AI reads the photos or plans as text. FieldIQ computes the quantities and prices them from your rate card." />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {!canUse ? (
            <EwPanel eyebrow="AI quotes" title="New draft"><EwEmpty>Only admins and office staff start AI quotes.</EwEmpty></EwPanel>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              {(capBudget || atCap) && <AiCapPrompt budget={capBudget ?? budget} />}
              <EwPanel eyebrow="AI quotes" title="What are you quoting?">
                <div className="grid gap-3 p-4 sm:grid-cols-2" role="radiogroup" aria-label="Quote kind">
                  {([['photo_demo', 'Photo → demolition', 'Building photos and the footprint. For a house or small building coming down.'],
                    ['plans_dig', 'Plans → dig', 'A foundation or site plan. For a basement or footing excavation.']] as const).map(([k, l, d]) => (
                    <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => switchKind(k)}
                      className={`rounded-fi-control border p-3 text-left ${kind === k ? 'border-fi-amber bg-fi-amber/10' : 'border-fieldiq-border bg-fieldiq-card'}`}>
                      <p className="font-bold text-white">{l}</p>
                      <p className="mt-1 text-xs text-fieldiq-muted">{d}</p>
                    </button>
                  ))}
                </div>
              </EwPanel>
              <EwPanel eyebrow="Draft" title="Customer and files">
                <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                  <EwField label="Customer">
                    <select className={EW_INPUT} value={customerId} onChange={(e) => { setCustomerId(e.target.value); setSiteId(''); }} aria-label="Customer">
                      <option value="">Choose a customer</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </EwField>
                  <EwField label="Site (optional)">
                    <select className={EW_INPUT} value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={!customerId} aria-label="Site">
                      <option value="">No site</option>
                      {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </EwField>
                  <EwField label="Title (optional)">
                    <input className={EW_INPUT} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} aria-label="Title" />
                  </EwField>
                  <div className="sm:col-span-2 lg:col-span-3">
                    <EwField label={kind === 'photo_demo' ? 'Building photos (1–4)' : 'Plans (one PDF, up to 6 pages read)'}
                      hint={kind === 'photo_demo' ? 'JPEG, PNG or WebP, up to 10 MB each. Front, sides, and any basement windows.' : 'PDF or an image, up to 25 MB.'}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-fi-control border border-dashed border-fieldiq-border bg-fieldiq-dark px-3 py-3 text-sm text-fieldiq-text">
                        <Upload className="size-4" aria-hidden="true" />
                        {files.length ? files.map((f) => f.name).join(', ') : 'Choose files'}
                        <input type="file" className="sr-only" data-testid="ew-ai-files" multiple={fileLimit > 1}
                          accept={kind === 'photo_demo' ? PHOTO_TYPES : PLAN_TYPES}
                          onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, fileLimit))} />
                      </label>
                    </EwField>
                  </div>
                </div>
              </EwPanel>
              <EwPanel eyebrow="Measurements" title={kind === 'photo_demo' ? 'What the office knows' : 'Anything the plans may not show'}>
                <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                  {kind === 'photo_demo' ? (
                    <>
                      {text('length', 'Building length', lenHint)}
                      {text('width', 'Building width', lenHint)}
                      {text('storeys', 'Storeys above grade (optional)', 'Leave blank for the AI to read.')}
                      <EwField label="Construction type (optional)" hint="Leave blank for the AI to read.">
                        <select className={EW_INPUT} value={inputs.construction_type ?? ''} onChange={(e) => set('construction_type', e.target.value)} aria-label="Construction type">
                          <option value="">Let the AI read it</option>
                          {CONSTRUCTION.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </EwField>
                      <EwField label="Basement (optional)" hint="Leave blank for the AI to read.">
                        <select className={EW_INPUT} value={inputs.basement ?? ''} onChange={(e) => set('basement', e.target.value)} aria-label="Basement">
                          <option value="">Let the AI read it</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </EwField>
                    </>
                  ) : (
                    <>
                      {text('depth', 'Depth below grade (optional)', lenHint)}
                      {text('over_dig', 'Over-dig each side (optional)', lenHint)}
                      {text('slope', 'Side slope H:V (optional)', 'e.g. 1:1 or 1.5:1')}
                    </>
                  )}
                </div>
              </EwPanel>
              <EwFormError message={error} />
              <div className="flex justify-end">
                <button type="submit" className={EW_PRIMARY} disabled={create.isPending || atCap}>
                  {create.isPending ? 'Uploading…' : 'Read with AI'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
